<template>
  <div>
    <v-dialog
      :persistent="true"
      :value="value"
      max-width="50rem"
      @input="val => $emit('input', val)">
      <v-card>
        <v-card-title>
          <v-layout justify-center>
            <div class="headline">{{ issue.id | formatIId }}</div>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <v-form v-model="valid">
            <v-container grid-list-md>
              <v-layout>
                <v-flex>
                  <v-textarea
                    v-model="editedIssue.description"
                    label="Description"
                    auto-grow
                    rows="1"/>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex>
                  <v-autocomplete
                    v-model="editedIssue.site"
                    :disabled="loadingSites"
                    :items="zabbixSites"
                    :filter="sitesFilter"
                    :loading="loadingSites"
                    :rules="[v => !!v || 'Site is required']"
                    item-text="id"
                    item-value="id"
                    label="Site"/>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex>
                  <v-autocomplete
                    v-model="editedIssue.category.id"
                    :disabled="categories.length === 0"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Category"/>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex>
                  <v-autocomplete
                    v-model="editedIssue.status.id"
                    :disabled="issueStatuses.length === 0"
                    :items="issueStatuses"
                    item-text="name"
                    item-value="id"
                    label="Status"/>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex v-if="editedIssue.dateClosed === null">
                  <v-text-field
                    :value="dateRaisedDisplay"
                    label="Raising"
                    @click="dateRaisedDialog = true"/>
                </v-flex>
                <v-flex v-else>
                  <v-text-field
                    :value="dateClosedDisplay"
                    label="Closure"
                    @click="dateClosedDialog = true"/>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :disabled="posting"
            flat
            @click="$emit('input', false)">
            Cancel
          </v-btn>
          <v-btn
            :disabled="posting || !valid"
            flat
            @click="save">
            <v-progress-circular
              v-if="posting"
              indeterminate/>
            <span v-else>Save</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- RAISING DATE PICKER DIALOG -->
    <picker-dialog
      v-model="dateRaisedDialog"
      :date-iso.sync="editedIssue.dateRaised"/>
    <!-- CLOSURE DATE PICKER DIALOG -->
    <picker-dialog
      v-model="dateClosedDialog"
      :date-iso.sync="editedIssue.dateClosed"/>
  </div>
</template>

<script>
/*
 * Displays the date in a readable format
 * moment has many formatting options, see:
 * https://momentjs.com/docs/#/displaying
 */
import moment from "moment";
import PickerDialog from "../SharedComponents/PickerDialog";

export default {
  name: "edit-dialog",
  components: { PickerDialog },
  props: {
    // Initial issue object will be copied when dialog opens up (see watch)
    issue: {
      required: true,
      type: Object
    },
    value: {
      required: true,
      type: Boolean
    }
  },
  data () {
    return {

      // Known cat. / stat. to the DB
      categories: [],
      issueStatuses: [],

      dateRaisedDialog: false,
      dateClosedDialog: false,

      // Buffered issue object while editing
      editedIssue: {
        category: {},
        status: {},
        dateRaised: null,
        dateClosed: null
      },

      loadingSites: false,
      posting: false,

      valid: false,

      // Production sites (obtained with Zabbix's API)
      zabbixSites: []

    };
  },
  computed: {
    dateRaisedDisplay () {
      return this.editedIssue.dateRaised
        ? moment(this.editedIssue.dateRaised).format("LLL") : "...";
    },
    dateClosedDisplay () {
      return this.editedIssue.dateClosed
        ? moment(this.editedIssue.dateClosed).format("LLL") : "...";
    }
  },
  watch: {
    value (open) {
      if (open) {
        this.editedIssue = {
          site: this.issue.site,
          description: this.issue.description,
          dateRaised: this.issue.dateRaised,
          dateClosed: this.issue.dateClosed,
          category: this.issue.category
            ? Object.assign({}, this.issue.category) : {},
          status: this.issue.status
            ? Object.assign({}, this.issue.status) : {}
        };
      }
    }
  },
  created () {
    // Get the production sites
    this.loadingSites = true;
    this.$http.
      get("/api/sites").
      then((r) => this.zabbixSites = Array.from(r.data)).
      finally(() => this.loadingSites = false);
    // Get the categories
    this.$http.
      get("/api/categories").
      then((r) => this.categories = Array.from(r.data));
    // Get the statuses
    this.$http.
      get("/api/issue_statuses").
      then((r) => this.issueStatuses = Array.from(r.data));
  },
  methods: {
    sitesFilter (item, query) {
      // Case insensitive filter on name
      let q = query ? query : "";
      let h = item.id ? item.id : "";
      return h.toLowerCase().indexOf(q.toLowerCase()) > -1;
    },
    // When the SAVE button of the edition dialog is clicked
    save () {
      const updateObj = {
        description: this.editedIssue.description,
        categoryId: this.editedIssue.category.id,
        statusId: this.editedIssue.status.id,
        site: this.editedIssue.site,
        dateRaised:
          moment(this.editedIssue.dateRaised).
            format("YYYY-MM-DDTHH:mm:ss"),
        dateClosed: this.editedIssue.dateClosed
          ? moment(this.editedIssue.dateClosed).
            format("YYYY-MM-DDTHH:mm:ss")
          : null
      };
      this.posting = true;
      this.$http.
        put(`/api/issues/${this.issue.id}`, updateObj).
        then(() => this.$emit("saved")).
        finally(() => this.posting = false);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Issue/EditDialog.vue